// views/Settings/settingsCatalogue.js
// ─────────────────────────────────────────────────────────────────────────────
// THE map of platform settings: which section each lives in, which editor
// renders it, and what the signed-in person needs to change it.
//
// Izi, 31 Oct 2026: settings were scattered across SHOP, Delivery, BRANDS,
// Ticketing, WALLET, Notifications and Audit & Compliance, four had no screen
// at all, and each route rolled its own super-admin check. This catalogue and
// SettingsSectionPage replace that with one SETTINGS group. The editors are the
// existing screens, rendered inside the section page (SettingsPanel switches to
// its embedded heading there), so nothing is rewritten; the old paths redirect.
//
// Each tab:
//   key          query-string id (?tab=)
//   label        tab text
//   description  one line under the section band
//   component    the editor; receives { canEdit }
//   level        what changing it needs: "update" (Admin) or "manage" (super admin)
//   keys         PlatformSetting keys it owns, for "last changed by"
//   href         a link-out instead of an embedded editor (lives elsewhere)
//   legacyPath   the old /admin route, kept as a redirect
//
// A section may sit under another sidebar menu instead of SETTINGS (Izi,
// 1 Nov 2026: Commission & tax belongs under Tax). It then carries
//   menu         the sidebar group that lists it ("tax")
//   routePath    its route under /admin ("/tax/commission-tax")
//   sidebarIcon  the icon it shows in that group
// It is still a Settings section page: same tabs, access levels and editors.
// ─────────────────────────────────────────────────────────────────────────────
import MinimumOrderValueSettings from "./MinimumOrderValueSettings";
import GrowthOffersSettings from "./GrowthOffersSettings";
import GroupOrderSettings from "./GroupOrderSettings";
import SupportSlaSettings from "./SupportSlaSettings";
import PlatformFiscalIdentitySettings from "./PlatformFiscalIdentitySettings";
import WalletCommissionRatesSettings from "./WalletCommissionRatesSettings";
import TreasuryThresholdsSettings from "./TreasuryThresholdsSettings";
import CodPolicySettings from "../CodPolicySettings";
import DeliveryFeeSettings from "../DeliveryFeeSettings";
import PaymentFeeSettings from "../PaymentFeeSettings";
import TicketingSettings from "../TicketingSettings";
import TicketingProviders from "../TicketingProviders";
import NotificationsCenter from "../Notifications/NotificationsCenter";
import ArtworkTemplateSettings from "../ArtworkTemplateSettings";
import VendorMediaAdmin from "../VendorMediaAdmin";
import SeedConfigView from "../SeedConfigView";
import FeatureEntitlementsView from "../FeatureEntitlementsView";
import RBACManager from "../RBACManager";
import VendorTaxSettingsView from "../VendorTaxSettingsView";

export const SETTINGS_BASE = "/admin/settings";

export const SETTINGS_SECTIONS = [
  {
    key: "orders",
    label: "Orders & checkout",
    icon: "fa-basket-shopping",
    description: "The checks every delivered order passes at checkout: the smallest basket per vendor, when cash on delivery is allowed, the free delivery Lusuku pays for on first and referred orders, and ordering together as a group.",
    requiredPermission: "settings:read",
    tabs: [
      { key: "minimum-order", label: "Minimum order value", level: "manage", component: MinimumOrderValueSettings, keys: ["minimum_order_value_UG", "minimum_order_value_ZM"] },
      { key: "cod-limit", label: "Cash on delivery limit", level: "manage", component: CodPolicySettings, keys: ["cod_max_product_amount"], legacyPath: "/cod-policy" },
      { key: "free-delivery-offers", label: "Free delivery offers", level: "manage", component: GrowthOffersSettings, keys: ["growth_offers_UG", "growth_offers_ZM"] },
      { key: "order-together", label: "Order together", level: "manage", component: GroupOrderSettings, keys: ["group_ordering_UG", "group_ordering_ZM"] },
    ],
  },
  {
    key: "delivery",
    label: "Delivery",
    icon: "fa-motorcycle",
    description: "How the server prices a delivery from the map: the urban fare, pickup difficulty and the delivery partner split. Other zones, the cost floor, surge, vehicle sizes and adjustment rates are set on the server.",
    requiredPermission: "settings:read",
    tabs: [
      { key: "fee-engine", label: "Delivery fee engine", level: "manage", component: DeliveryFeeSettings, legacyPath: "/delivery-fee-settings" },
    ],
  },
  {
    key: "payments",
    label: "Payments & money",
    icon: "fa-money-bill-wave",
    description: "Who bears payment provider charges, what providers and MTN keep, the MTN float reserve, reconciliation and payout thresholds, and the finance report schedule.",
    requiredPermission: "settings:read",
    tabs: [
      { key: "rail-fees", label: "Payment processing fees", level: "manage", component: PaymentFeeSettings, keys: ["payment_rail_fee_policy", "payment_rail_rates"], legacyPath: "/payment-fees" },
      { key: "thresholds", label: "Reconciliation and payout thresholds", level: "manage", component: TreasuryThresholdsSettings },
      { key: "float", label: "MTN float reserve", level: "manage", href: "/admin/wallet-dashboard?tab=disbursements&open=float", hrefLabel: "Open in Wallet", description: "The balance payouts may not spend, and whether a breach blocks the payout. Edited on the Wallet treasury strip." },
      { key: "collection-fee", label: "MTN collection tariff", level: "manage", href: "/admin/wallet-dashboard?tab=moneyIn", hrefLabel: "Open Money In", description: "The percent MTN keeps on every MoMo payment; the wallet's books use it. Keep the MoMo row of the provider rate card equal to it. Edited on the Money In tab." },
      { key: "reports", label: "Finance reports and recipients", level: "update", href: "/admin/wallet-dashboard?tab=reports", hrefLabel: "Open Reports", description: "Report schedule, automatic reconciliation, recipients and CSV attachments. Edited on the Reports tab." },
    ],
  },
  {
    key: "commission-tax",
    menu: "tax",
    routePath: "/tax/commission-tax",
    sidebarIcon: "nc-icon nc-settings-tool-66",
    label: "Commission & tax",
    icon: "fa-percent",
    description: "The commission Lusuku takes from each vendor, each vendor's VAT and EFRIS position, and Lusuku's own identity with URA.",
    requiredPermission: "tax:read",
    tabs: [
      { key: "default-commission", label: "Wallet fallback commission", level: "manage", component: WalletCommissionRatesSettings },
      { key: "vendor-contracts", label: "Vendor contracts", level: "manage", href: "/admin/wallet-dashboard?tab=commission", hrefLabel: "Open vendor contracts", description: "Each vendor's negotiated commission and VAT registration, applied to new orders. Edited on the Wallet Commission tab by a super user." },
      { key: "vendor-tax", label: "Vendor EFRIS identity", level: "manage", component: VendorTaxSettingsView, legacyPath: "/vendor-tax-settings" },
      { key: "lusuku-fiscal-identity", label: "Lusuku's fiscal identity", level: "manage", component: PlatformFiscalIdentitySettings, keys: ["fiscal.seller.UG", "fiscal.seller.ZM"] },
    ],
  },
  {
    key: "ticketing",
    label: "Ticketing",
    icon: "fa-ticket",
    description: "Whether customers can buy event tickets online and where, how long picked seats stay reserved, and the credentials Lusuku uses to call a cinema's seat system.",
    requiredPermission: "settings:read",
    tabs: [
      { key: "pilot", label: "Ticketing switch and seat holds", level: "manage", component: TicketingSettings, keys: ["ticketing_enabled", "ticketing_enabled_countries", "ticketing_hold_policy", "ticketing_hold_policy_bands"], legacyPath: "/ticketing/settings" },
      { key: "providers", label: "Cinema seat API credentials", level: "update", component: TicketingProviders, legacyPath: "/ticketing/providers" },
    ],
  },
  {
    key: "notifications",
    label: "Notifications & messaging",
    icon: "fa-bell",
    description: "How dashboards alert staff and vendors to new orders and new sellers in each market: sound, browser push and pop ups. Vendors set their own email, SMS and WhatsApp alerts on their Alerts page.",
    requiredPermission: "settings:read",
    tabs: [
      { key: "operator-alerts", label: "Dashboard order alerts", level: "update", component: NotificationsCenter, legacyPath: "/notifications" },
    ],
  },
  {
    key: "catalogue",
    label: "Catalogue & media",
    icon: "fa-images",
    description: "Templates vendors download, how many images each vendor gets before buying more, and whether the web landing page shows Featured Brands.",
    requiredPermission: "settings:read",
    tabs: [
      { key: "artwork", label: "Artwork templates", level: "update", component: ArtworkTemplateSettings, legacyPath: "/product-artwork-template" },
      { key: "media-billing", label: "Image allowance and billing", level: "manage", component: VendorMediaAdmin, keys: ["vendor_media_included_images", "vendor_media_enforcement_mode"], legacyPath: "/media-billing" },
      { key: "featured-brands", label: "Landing page Featured Brands", level: "update", href: "/admin/brands-management", hrefLabel: "Open Manage Brands", description: "Shows or hides the Featured Brands carousel on the web landing page for every market. Each brand also needs its own Featured and On Landing switches. Edited on Manage Brands." },
    ],
  },
  {
    key: "support",
    label: "Support & programmes",
    icon: "fa-headset",
    description: "Support response targets, the seed credit programme, and which vendor teams may use the audit tools.",
    requiredPermission: "settings:read",
    tabs: [
      { key: "sla", label: "Support response targets", level: "update", component: SupportSlaSettings, keys: ["support_case_sla"] },
      { key: "seed-credits", label: "Seed credits", level: "update", component: SeedConfigView, legacyPath: "/seed-config" },
      { key: "entitlements", label: "Vendor audit tools", level: "update", component: FeatureEntitlementsView, legacyPath: "/feature-entitlements" },
    ],
  },
  {
    key: "access",
    label: "Access control",
    icon: "fa-user-shield",
    description: "Lusuku staff roles, per person overrides and who may change settings. Vendor teams are managed on each vendor's Team page.",
    requiredPermission: "access_control:read",
    tabs: [
      { key: "staff", label: "Staff roles and permissions", level: "manage", component: RBACManager, legacyPath: "/access-control" },
    ],
  },
];

export const sectionByKey = (key) => SETTINGS_SECTIONS.find((s) => s.key === key) || null;

/** A section's route under /admin: its own routePath, or /settings/<key>. */
export const sectionRoutePath = (section) => section.routePath || `/settings/${section.key}`;

/** The address of a section page, optionally opened on one tab. */
export const sectionUrl = (section, tabKey) =>
  `/admin${sectionRoutePath(section)}${tabKey ? `?tab=${tabKey}` : ""}`;

/** The sections the SETTINGS sidebar group lists; the rest sit under their own menu. */
export const SETTINGS_MENU_SECTIONS = SETTINGS_SECTIONS.filter((s) => !s.menu);

/** The sections another sidebar group lists, e.g. sectionsInMenu("tax"). */
export const sectionsInMenu = (menu) => SETTINGS_SECTIONS.filter((s) => s.menu === menu);

/** Every old route that now lives in Settings, with where it went. */
export const SETTINGS_REDIRECTS = SETTINGS_SECTIONS.flatMap((s) =>
  s.tabs.filter((t) => t.legacyPath).map((t) => ({ from: t.legacyPath, to: sectionUrl(s, t.key) })),
);
